import SEO from "@/components/SEO";
import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/pages/home/components/Footer";
import { artistas } from "@/mocks/artistas";
import { albuns } from "@/mocks/albuns";
import { musicas, TONS_DISPONIVEIS } from "@/mocks/musicas";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

interface ProductMap {
  [key: string]: number;
}

export default function AlbumPage() {
  const navigate = useNavigate();
  const { artistaId, albumId } = useParams<{ artistaId: string; albumId: string }>();
  const { user, profile } = useAuth();
  const siteUrl = import.meta.env.VITE_SITE_URL || "https://example.com";
  const [previewAberto, setPreviewAberto] = useState<string | null>(null);
  const [productMap, setProductMap] = useState<ProductMap>();
  const [comprando, setComprando] = useState<string | null>(null);
  const [toast, setToast] = useState<{ type: "success" | "error"; msg: string } | null>(null);
  const [tomSelecionado, setTomSelecionado] = useState<Record<string, string>>();
  const [checkoutAddress, setCheckoutAddress] = useState<Record<string, string> | null>(null);

  const artista = artistas.find((a) => a.id === artistaId);
  const album = albuns.find((a) => a.id === albumId && a.artistaId === artistaId);
  const musicasAlbum = musicas.filter(
    (m) => m.albumId === albumId && m.artistaId === artistaId
  );

  const schema = artista && album
    ? {
        "@context": "https://schema.org",
        "@type": "MusicAlbum",
        name: album.titulo,
        url: `${siteUrl}/biblioteca/${artista.id}/${album.id}`,
        byArtist: {
          "@type": "MusicGroup",
          name: artista.nome,
        },
        numTracks: musicasAlbum.length,
        datePublished: album.ano,
        description: album.descricao,
        inLanguage: "pt-BR",
      }
    : undefined;

  useEffect(() => {
    async function buscarProdutos() {
      const { data } = await supabase
        .from("product_items")
        .select("id, name, media")
        .eq("category_id", 1)
        .eq("status", "active");
      if (data) {
        const map: ProductMap = {};
        data.forEach((p: any) => {
          const musicaId = p.media?.[0]?.musicaId;
          if (musicaId) {
            map[musicaId] = p.id;
          } else {
            map[p.name.toLowerCase().trim()] = p.id;
          }
        });
        setProductMap(map);
      }
    }
    buscarProdutos();

    // Carregar endereço do perfil para checkout
    if (profile?.endereco && typeof profile.endereco === "object") {
      setCheckoutAddress(profile.endereco as Record<string, string>);
    }

    // Inicializar tons selecionados com o tom original de cada música
    const inicial: Record<string, string> = {};
    musicasAlbum.forEach((m) => {
      inicial[m.id] = m.tomOriginal;
    });
    setTomSelecionado(inicial);
  }, [artistaId, albumId, profile]);

  async function handleComprar(titulo: string, musicaId: string) {
    if (!user) {
      navigate(`/login?redirect=/biblioteca/${artistaId}/${albumId}`);
      return;
    }
    const productId = productMap[musicaId];
    if (!productId) {
      setToast({ type: "error", msg: "Partitura ainda não disponível para compra." });
      return;
    }

    const tom = tomSelecionado[musicaId] || "C";

    setComprando(titulo);
    try {
      const { data, error } = await supabase.functions.invoke('create-stripe-checkout', {
        body: {
          items: [{ product_id: productId, quantity: 1 }],
          user_id: user.id,
          customer_email: user.email,
          address: checkoutAddress,
          metadata: { tone: tom },
        },
      });
      if (error || !data || !data.url) {
        setToast({ type: "error", msg: data?.error || error?.message || "Erro ao iniciar pagamento." });
      } else {
        window.location.href = data.url;
      }
    } catch (err: any) {
      setToast({ type: "error", msg: err.message || "Erro ao processar compra." });
    } finally {
      setComprando(null);
    }
  }

  if (!artista || !album) {
    return (
      <div className="min-h-screen bg-godoy-black">
        <Navbar />
        <div className="pt-32 pb-20 text-center">
          <h1 className="font-serif text-2xl text-godoy-cream">Álbum não encontrado</h1>
          <Link to="/biblioteca" className="text-godoy-gold mt-4 inline-block">
            Voltar para Biblioteca
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <>
      {schema && (
        <SEO
          title={`${album.titulo} - ${artista.nome} | Partituras Gospel`}
          description={`Baixe as partituras do álbum ${album.titulo} de ${artista.nome}. ${musicasAlbum.length} músicas disponíveis em PDF na GodoyProd.`}
          keywords={`${album.titulo}, ${artista.nome}, partituras gospel, partituras PDF, álbum gospel`}
          schema={schema}
          canonicalPath={`/biblioteca/${artista.id}/${album.id}`}
        />
      )}
      <div className="min-h-screen bg-godoy-black">
        <Navbar />

        <section className="pt-32 pb-20 md:pt-40 md:pb-28">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-5xl mx-auto">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm text-godoy-lightgray mb-8">
              <Link to="/biblioteca" className="hover:text-godoy-gold transition-colors">
                Biblioteca
              </Link>
              <i className="ri-arrow-right-s-line" />
              <Link
                to={`/biblioteca/${artista.id}`}
                className="hover:text-godoy-gold transition-colors"
              >
                {artista.nome}
              </Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-godoy-cream">{album.titulo}</span>
            </div>

            {/* Header Álbum */}
            <div className="flex flex-col md:flex-row items-center md:items-start gap-8 mb-12">
              <div className="w-48 h-48 md:w-60 md:h-60 rounded-lg overflow-hidden border border-godoy-gray/30 flex-shrink-0">
                <img
                  src={album.imagem}
                  alt={album.titulo}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="text-center md:text-left">
                <span className="text-godoy-gold text-sm font-semibold tracking-widest uppercase">
                  {album.ano}
                </span>
                <h1 className="font-serif text-3xl md:text-4xl font-bold text-godoy-cream mt-2">
                  {album.titulo}
                </h1>
                <p className="text-godoy-lightgray text-lg mt-2">
                  {artista.nome}
                </p>
                <p className="text-godoy-lightgray/70 text-sm mt-3 max-w-md">
                  {album.descricao}
                </p>
                <p className="text-godoy-lightgray text-sm mt-4">
                  {musicasAlbum.length} músicas
                </p>
              </div>
            </div>

            {/* Lista de Músicas */}
            <div className="bg-godoy-charcoal/40 border border-godoy-gray/20 rounded-lg overflow-hidden">
              <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-godoy-gray/20 text-xs text-godoy-lightgray uppercase tracking-wider">
                <div className="col-span-1">#</div>
                <div className="col-span-5 md:col-span-5">Música</div>
                <div className="col-span-2 text-center hidden md:block">Duração</div>
                <div className="col-span-2 text-center hidden md:block">Tom Orig.</div>
                <div className="col-span-2 md:col-span-2 text-right">Preço</div>
              </div>

              {musicasAlbum.map((musica, index) => (
                <div
                  key={musica.id}
                  className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-godoy-gray/10 items-center hover:bg-godoy-gray/20 transition-colors"
                >
                  <div className="col-span-1 text-godoy-lightgray text-sm">
                    {index + 1}
                  </div>
                  <div className="col-span-5 md:col-span-5">
                    <span className="text-godoy-cream font-medium text-sm block">
                      {musica.titulo}
                    </span>
                    <span className="text-godoy-lightgray/60 text-xs block md:hidden">
                      {musica.duracao} · {musica.tomOriginal}
                    </span>
                  </div>
                  <div className="col-span-2 text-center text-godoy-lightgray text-sm hidden md:block">
                    {musica.duracao}
                  </div>
                  <div className="col-span-2 text-center text-godoy-gold text-sm hidden md:block font-medium">
                    {musica.tomOriginal}
                  </div>
                  <div className="col-span-2 md:col-span-2 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {musica.previewDisponivel && (
                        <button
                          onClick={() =>
                            setPreviewAberto(
                              previewAberto === musica.id ? null : musica.id
                            )
                          }
                          className="w-8 h-8 flex items-center justify-center rounded-full bg-godoy-gold/10 text-godoy-gold hover:bg-godoy-gold/20 transition-all"
                          title="Visualizar preview"
                        >
                          <i className="ri-eye-line text-sm" />
                        </button>
                      )}
                      <span className="text-godoy-gold font-semibold text-sm">
                        R$ {musica.preco.toFixed(2).replace('.', ',')}
                      </span>
                    </div>
                  </div>

                  {/* Preview expandido */}
                  {previewAberto === musica.id && (
                    <div className="col-span-12 mt-2 py-4 px-4 bg-godoy-black/60 rounded-lg border border-godoy-gold/20">
                      <div className="flex flex-col md:flex-row items-start gap-6">
                        <div className="w-full md:w-48 h-64 bg-godoy-gray/30 rounded border border-godoy-gray/40 flex items-center justify-center flex-shrink-0">
                          <div className="text-center p-4">
                            <i className="ri-file-pdf-line text-4xl text-godoy-gold/50 mb-2" />
                            <p className="text-godoy-lightgray text-xs">
                              Preview da Partitura
                            </p>
                            <p className="text-godoy-cream text-sm font-medium mt-1">
                              {musica.titulo}
                            </p>
                            <p className="text-godoy-lightgray text-xs mt-1">
                              Página 1 de {musica.paginas}
                            </p>
                          </div>
                        </div>
                        <div className="flex-1 text-center md:text-left w-full">
                          <p className="text-godoy-cream font-medium mb-2 text-lg">
                            {musica.titulo} — {artista.nome}
                          </p>

                          {/* Metadados da música */}
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                            <div className="bg-godoy-charcoal/60 rounded-md px-3 py-2 text-center">
                              <p className="text-godoy-lightgray text-xs uppercase tracking-wider">BPM</p>
                              <p className="text-godoy-cream font-semibold text-sm">{musica.bpm}</p>
                            </div>
                            <div className="bg-godoy-charcoal/60 rounded-md px-3 py-2 text-center">
                              <p className="text-godoy-lightgray text-xs uppercase tracking-wider">Tom Original</p>
                              <p className="text-godoy-gold font-semibold text-sm">{musica.tomOriginal}</p>
                            </div>
                            <div className="bg-godoy-charcoal/60 rounded-md px-3 py-2 text-center">
                              <p className="text-godoy-lightgray text-xs uppercase tracking-wider">Versão</p>
                              <p className="text-godoy-cream font-semibold text-sm">{musica.versao}</p>
                            </div>
                            <div className="bg-godoy-charcoal/60 rounded-md px-3 py-2 text-center">
                              <p className="text-godoy-lightgray text-xs uppercase tracking-wider">Ano</p>
                              <p className="text-godoy-cream font-semibold text-sm">{musica.anoLancamento}</p>
                            </div>
                          </div>

                          <p className="text-godoy-lightgray text-sm mb-4">
                            Partitura completa com notação profissional em {musica.paginas} páginas.
                            Disponível em PDF pronto para impressão ou leitura digital.
                          </p>

                          {/* Seletor de TOM */}
                          <div className="mb-4">
                            <label className="text-godoy-lightgray text-xs uppercase tracking-wider block mb-2">
                              Selecione o Tom desejado:
                            </label>
                            <div className="flex flex-wrap gap-2">
                              {TONS_DISPONIVEIS.map((tom) => (
                                <button
                                  key={tom}
                                  onClick={() =>
                                    setTomSelecionado((prev) => ({ ...prev, [musica.id]: tom }))
                                  }
                                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all border ${
                                    tomSelecionado[musica.id] === tom
                                      ? "bg-godoy-gold text-godoy-black border-godoy-gold"
                                      : "bg-godoy-charcoal/60 text-godoy-cream border-godoy-gray/30 hover:border-godoy-gold/50"
                                  }`}
                                >
                                  {tom}
                                </button>
                              ))}
                            </div>
                            <p className="text-godoy-lightgray/60 text-xs mt-2">
                              Tom selecionado: <span className="text-godoy-gold font-semibold">{tomSelecionado[musica.id] || musica.tomOriginal}</span>
                            </p>
                          </div>

                          <p className="text-godoy-gold text-xs mb-4">
                            <i className="ri-instagram-line mr-1" />
                            Comprou? Marque <strong>@eugoodoy</strong> no Instagram que eu reposto!
                          </p>
                          <div className="flex flex-col sm:flex-row items-center gap-3 justify-center md:justify-start">
                            <button
                              onClick={() => handleComprar(musica.titulo, musica.id)}
                              disabled={comprando === musica.titulo}
                              className="px-6 py-2.5 bg-godoy-gold text-godoy-black font-semibold rounded-md hover:bg-godoy-goldlight transition-all text-sm whitespace-nowrap disabled:opacity-60"
                            >
                              {comprando === musica.titulo ? "Processando..." : `Comprar R$ ${musica.preco.toFixed(2).replace('.', ',')}`}
                            </button>
                            {!user && (
                              <Link
                                to="/login"
                                className="px-6 py-2.5 border border-godoy-gold/50 text-godoy-gold rounded-md hover:bg-godoy-gold/10 transition-all text-sm whitespace-nowrap"
                              >
                                Entrar para comprar
                              </Link>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Toast */}
        {toast && (
          <div className="fixed bottom-6 right-6 z-[150] max-w-sm">
            <div
              className={`px-5 py-3 rounded-md text-sm font-medium border ${
                toast.type === "success"
                  ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400"
                  : "bg-red-500/10 border-red-500/30 text-red-400"
              }`}
            >
              <div className="flex items-center gap-2">
                <i className={toast.type === "success" ? "ri-check-line" : "ri-error-warning-line"} />
                {toast.msg}
                <button onClick={() => setToast(null)} className="ml-2 text-godoy-lightgray hover:text-godoy-cream">
                  <i className="ri-close-line" />
                </button>
              </div>
            </div>
          </div>
        )}

        <Footer />
      </div>
    </>
  );
}